/**
 * Hook for fetching and managing pending classifications
 */

import { useState, useEffect, useCallback } from 'react';
import type {
  PendingClassification,
  ResolveClassificationData,
} from '../types/management';
import * as api from '../lib/management-api';
import { useAuth } from '../components/Auth';

interface UsePendingClassificationsResult {
  pending: PendingClassification[];
  isLoading: boolean;
  error: string | null;
  hasFetched: boolean;
  refetch: () => void;
  resolve: (id: string, data: ResolveClassificationData) => Promise<void>;
  dismiss: (id: string) => Promise<void>;
}

/**
 * Hook to fetch and manage pending classifications
 *
 * @returns Object with pending data, loading state, error, and operations
 */
export function usePendingClassifications(): UsePendingClassificationsResult {
  const { getAuthHeader, isAuthenticated } = useAuth();
  const [pending, setPending] = useState<PendingClassification[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [hasFetched, setHasFetched] = useState(false);
  const [fetchTrigger, setFetchTrigger] = useState(0);

  const refetch = useCallback(() => {
    setFetchTrigger((prev) => prev + 1);
  }, []);

  useEffect(() => {
    if (!isAuthenticated) {
      setIsLoading(false);
      setError('Authentication required');
      return;
    }

    const abortController = new AbortController();

    async function loadPending() {
      setIsLoading(true);
      setError(null);

      try {
        const authHeader = getAuthHeader();
        if (!authHeader) {
          throw new Error('No authentication token available');
        }

        const data = await api.fetchPendingClassifications(
          { Authorization: authHeader },
          abortController.signal,
        );
        setPending(data);
        setHasFetched(true);
      } catch (err) {
        if (err instanceof Error && err.name === 'AbortError') {
          return; // Ignore abort errors
        }
        setError(
          err instanceof Error
            ? err.message
            : 'Failed to fetch pending classifications',
        );
      } finally {
        setIsLoading(false);
      }
    }

    loadPending();

    return () => {
      abortController.abort();
    };
  }, [isAuthenticated, getAuthHeader, fetchTrigger]);

  const resolve = useCallback(
    async (id: string, data: ResolveClassificationData): Promise<void> => {
      const authHeader = getAuthHeader();
      if (!authHeader) {
        throw new Error('Not authenticated');
      }

      await api.resolvePendingClassification(id, data, {
        Authorization: authHeader,
      });
      setPending((prev) => prev.filter((p) => p.id !== id));
    },
    [getAuthHeader],
  );

  const dismiss = useCallback(
    async (id: string): Promise<void> => {
      const authHeader = getAuthHeader();
      if (!authHeader) {
        throw new Error('Not authenticated');
      }

      await api.dismissPendingClassification(id, { Authorization: authHeader });
      setPending((prev) => prev.filter((p) => p.id !== id));
    },
    [getAuthHeader],
  );

  return {
    pending,
    isLoading,
    error,
    hasFetched,
    refetch,
    resolve,
    dismiss,
  };
}
